import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Text,
  Switch,
  Icon,
  useColorModeValue,
  VStack,
  HStack,
  Badge,
  Tooltip,
} from '@chakra-ui/react';
import { FaTrophy, FaChartSimple } from 'react-icons/fa6';
import { FiChevronDown } from 'react-icons/fi';
import Card from 'components/card/Card';
import { PermissionWrapper } from 'components/shared/PermissionWrapper';
import { apiGetDashboardStatus } from 'api';
import Storage from 'storage';
import { SalesContestDashboard } from '../sales_contest_dashboard/SalesContestDashboard';

// Gauge Component with 6-Part Gradient and Arrow
const GaugeComponent = ({
  value,
  label,
  isLarge = false,
  disabled = false,
}: {
  value: number;
  label: string;
  isLarge?: boolean;
  disabled?: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 10, // Position closer to cursor
    });
  };

  // Calculate color based on value
  const getColor = (val: number) => {
    if (val < 40)
      return { main: '#EF4444', light: '#FEE2E2', glow: '#EF444440' }; // Red
    if (val < 60)
      return { main: '#F59E0B', light: '#FEF3C7', glow: '#F59E0B40' }; // Orange
    if (val < 80)
      return { main: '#EAB308', light: '#FEF9C3', glow: '#EAB30840' }; // Yellow
    return { main: '#10B981', light: '#D1FAE5', glow: '#10B98140' }; // Green
  };

  const textColor = useColorModeValue('secondaryGray.900', 'white') as string;
  const colors = disabled
    ? { main: '#CBD5E0', light: '#E2E8F0', glow: '#CBD5E040' }
    : getColor(value);
  const height = isLarge ? '20px' : '12px';

  return (
    <Box
      textAlign="center"
      w="100%"
      position="relative"
      opacity={disabled ? 0.4 : 1}
    >
      {/* Percentage and Label */}
      <Box mb={isLarge ? '8px' : '6px'}>
        <Text
          fontSize={isLarge ? '4xl' : '2xl'}
          fontWeight="bold"
          color={textColor}
        >
          {value}%
        </Text>
        {label && (
          <Text
            fontSize={isLarge ? 'sm' : 'xs'}
            color={textColor}
            mt={isLarge ? '1' : '0.5'}
          >
            {label}
          </Text>
        )}
      </Box>

      {/* Progress Bar Container */}
      <Box
        position="relative"
        w="100%"
        mb="3"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onMouseMove={handleMouseMove}
        cursor="pointer"
      >
        {/* Background bar */}
        <Box
          bg="gray.200"
          borderRadius="full"
          w="100%"
          h={height}
          position="relative"
          overflow="hidden"
        />

        {/* Progress bar with gradient */}
        <Box
          position="absolute"
          top="0"
          left="0"
          borderRadius="full"
          w={`${value}%`}
          h={height}
          bg={`linear-gradient(90deg, ${colors.main} 0%, ${colors.main}dd 100%)`}
          boxShadow={`0 0 ${isLarge ? '12px' : '8px'} ${colors.glow}`}
          transition="width 1s ease-out"
          overflow="hidden"
        >
          {/* Animated shine effect */}
          <Box
            position="absolute"
            top="0"
            left="0"
            w="100%"
            h="100%"
            bg="linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)"
            animation="pulse 2s infinite"
          />
        </Box>

        {/* Marker at value position */}
        <Box
          position="absolute"
          top="50%"
          transform="translate(-50%, -50%)"
          left={`${value}%`}
          w={isLarge ? '4px' : '3px'}
          h={isLarge ? '28px' : '20px'}
          bg={colors.main}
          borderRadius="2px"
          boxShadow={`0 2px ${isLarge ? '6px' : '4px'} ${colors.glow}`}
          transition="left 1s ease-out"
        />
      </Box>

      {/* Scale markers */}
      <Flex justify="space-between" w="100%" px="1">
        <Text fontSize="xs" color="gray.500" fontWeight="medium">
          0
        </Text>
        <Text fontSize="xs" color="gray.500" fontWeight="medium">
          25
        </Text>
        <Text fontSize="xs" color="gray.500" fontWeight="medium">
          50
        </Text>
        <Text fontSize="xs" color="gray.500" fontWeight="medium">
          75
        </Text>
        <Text fontSize="xs" color="gray.500" fontWeight="medium">
          100
        </Text>
      </Flex>

      {/* Tooltip */}
      {isHovered && (
        <Box
          position="absolute"
          left={`${tooltipPosition.x}px`}
          top={`${tooltipPosition.y}px`}
          bg="gray.800"
          color="white"
          px="3"
          py="2"
          borderRadius="md"
          fontSize="sm"
          fontWeight="medium"
          boxShadow="lg"
          zIndex="tooltip"
          pointerEvents="none"
          transform="translate(-50%, -100%)"
          whiteSpace="nowrap"
        >
          {label}: {value}%
          <Box
            position="absolute"
            top="100%"
            left="50%"
            transform="translateX(-50%)"
            w="0"
            h="0"
            borderLeft="6px solid transparent"
            borderRight="6px solid transparent"
            borderTop="6px solid"
            borderTopColor="gray.800"
          />
        </Box>
      )}
    </Box>
  );
};

// Metric Card Component
const MetricCard = ({
  title,
  value,
  disabled = false,
}: {
  title: string;
  value: string;
  disabled?: boolean;
}) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white') as string;
  const bgColor = useColorModeValue('white', 'navy.700') as string;
  const borderColor = useColorModeValue(
    '#E6ECFA',
    'rgba(135, 140, 189, 0.3)',
  ) as string;

  const cardProps = {
    bg: bgColor,
    p: '12px',
    borderRadius: '12px',
    border: '1px solid',
    borderColor: borderColor,
    textAlign: 'center' as const,
    minH: '60px',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    opacity: disabled ? 0.4 : 1,
  };

  return (
    <Card {...cardProps}>
      <Text fontSize="xs" color={textColor} mb="3px">
        {title}
      </Text>
      <Text fontSize="lg" fontWeight="bold" color={textColor}>
        {value}
      </Text>
    </Card>
  );
};

// Status Box Component
const StatusBox = ({
  count,
  label,
  isNewCount = 0,
  isComingSoon = false,
  onClick,
}: {
  count: number | string;
  label: string;
  isNewCount?: number;
  isComingSoon?: boolean;
  onClick?: () => void;
}) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white') as string;
  const normalBgColor = useColorModeValue(
    '#E6ECFA',
    'whiteAlpha.200',
  ) as string;
  const overdueBgColor = useColorModeValue('#FCE8E8', '#2E0505') as string;
  const comingSoonBgColor = useColorModeValue('#F3F4F6', 'gray.600') as string;
  const normalBorderColor = useColorModeValue(
    '#A1A4AD',
    'whiteAlpha.400',
  ) as string;
  const overdueBorderColor = useColorModeValue('#E31A1A', '#E31A1A') as string;
  const comingSoonBorderColor = useColorModeValue(
    '#9CA3AF',
    'gray.500',
  ) as string;

  const isOverdue = isNewCount > 0 && !isComingSoon;
  const bgColor = isComingSoon
    ? comingSoonBgColor
    : isOverdue
      ? overdueBgColor
      : normalBgColor;
  const borderColor = isComingSoon
    ? comingSoonBorderColor
    : isOverdue
      ? overdueBorderColor
      : normalBorderColor;

  const tooltipLabel = isComingSoon
    ? `Coming Soon - ${label}`
    : isNewCount > 0
      ? `${isNewCount} new ${label.toLowerCase()} item${
          isNewCount > 1 ? 's' : ''
        }`
      : `No new ${label.toLowerCase()} items`;

  return (
    <Tooltip
      label={tooltipLabel}
      placement="top"
      hasArrow
      bg="gray.800"
      color="white"
      fontSize="sm"
      borderRadius="md"
      px="3"
      py="2"
    >
      <Box
        w="90px"
        h="90px"
        bg={bgColor}
        border="2px solid"
        borderColor={borderColor}
        borderRadius="12px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        transition="all 0.2s"
        _hover={{
          transform: 'scale(1.05)',
          boxShadow: 'md',
        }}
        cursor={onClick ? 'pointer' : isComingSoon ? 'not-allowed' : 'pointer'}
        p="8px"
        position="relative"
        onClick={onClick}
      >
        {/* New Count Badge */}
        {isNewCount > 0 && (
          <Badge
            position="absolute"
            top="-8px"
            right="-8px"
            bg="red.500"
            color="white"
            borderRadius="full"
            minW="20px"
            h="20px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="xs"
            fontWeight="bold"
            boxShadow="md"
            zIndex="1"
          >
            {isNewCount > 99 ? '99+' : isNewCount}
          </Badge>
        )}

        {/* Count */}
        <Text
          color={textColor}
          fontSize="xl"
          fontWeight="bold"
          textAlign="center"
          lineHeight="1"
        >
          {count}
        </Text>

        {/* Label */}
        <Text
          color={textColor}
          fontSize="xs"
          fontWeight="500"
          textAlign="center"
          lineHeight="1.2"
          mt="4px"
        >
          {label}
        </Text>
      </Box>
    </Tooltip>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const textColor = useColorModeValue('secondaryGray.900', 'white') as string;
  const bgColor = useColorModeValue('white', 'navy.700') as string;
  const borderColor = useColorModeValue(
    '#E6ECFA',
    'rgba(135, 140, 189, 0.3)',
  ) as string;

  // User data state
  const [userData, setUserData] = useState<{
    first_name: string;
    last_name: string;
  } | null>(null);

  // Desk collapse state
  const [isDeskExpanded, setIsDeskExpanded] = useState(true);

  // Status data state
  const [statusData, setStatusData] = useState<{
    leads: { count: string | number; is_new_count: number };
    work_in_progress: { count: string | number; is_new_count: number };
    binders: { count: string | number; is_new_count: number };
    documents: { count: string | number; is_new_count: number };
    tasks: { count: string | number; is_new_count: number };
    renewals: { count: string | number; is_new_count: number };
    cancellations: { count: string | number; is_new_count: number };
    document_score?: number;
  }>({
    leads: { count: '-', is_new_count: 0 },
    work_in_progress: { count: '-', is_new_count: 0 },
    binders: { count: '-', is_new_count: 0 },
    documents: { count: '-', is_new_count: 0 },
    tasks: { count: '-', is_new_count: 0 },
    renewals: { count: '-', is_new_count: 0 },
    cancellations: { count: '-', is_new_count: 0 },
    document_score: 0,
  });

  // Load user data on component mount
  useEffect(() => {
    const user = Storage.get('user');
    if (user && user.first_name && user.last_name) {
      setUserData({
        first_name: user.first_name,
        last_name: user.last_name,
      });
    }
  }, []);

  // Fetch status data on component mount
  useEffect(() => {
    const fetchStatusData = async () => {
      try {
        const response = await apiGetDashboardStatus();
        // Handle different response structures
        const data = response.data || response;
        if (data && typeof data === 'object') {
          // Convert API data and handle isNewCount flags
          const processedData = {
            leads: {
              count:
                typeof data.leads?.count === 'number'
                  ? data.leads.count
                  : typeof data.leads === 'number'
                    ? data.leads
                    : '-',
              is_new_count:
                typeof data.leads?.is_new_count === 'number'
                  ? data.leads.is_new_count
                  : 0,
            },
            work_in_progress: {
              count:
                typeof data.work_in_progress?.count === 'number'
                  ? data.work_in_progress.count
                  : typeof data.work_in_progress === 'number'
                    ? data.work_in_progress
                    : '-',
              is_new_count:
                typeof data.work_in_progress?.is_new_count === 'number'
                  ? data.work_in_progress.is_new_count
                  : 0,
            },
            binders: {
              count:
                typeof data.binders?.count === 'number'
                  ? data.binders.count
                  : typeof data.binders === 'number'
                    ? data.binders
                    : '-',
              is_new_count:
                typeof data.binders?.is_new_count === 'number'
                  ? data.binders.is_new_count
                  : 0,
            },
            documents: {
              count:
                typeof data.documents?.count === 'number'
                  ? data.documents.count
                  : typeof data.documents === 'number'
                    ? data.documents
                    : '-',
              is_new_count:
                typeof data.documents?.is_new_count === 'number'
                  ? data.documents.is_new_count
                  : 0,
            },
            tasks: {
              count:
                typeof data.tasks?.count === 'number'
                  ? data.tasks.count
                  : typeof data.tasks === 'number'
                    ? data.tasks
                    : 0,
              is_new_count:
                typeof data.tasks?.is_new_count === 'number'
                  ? data.tasks.is_new_count
                  : 0,
            },
            renewals: {
              count:
                typeof data.renewals?.count === 'number'
                  ? data.renewals.count
                  : typeof data.renewals === 'number'
                    ? data.renewals
                    : 0,
              is_new_count:
                typeof data.renewals?.is_new_count === 'number'
                  ? data.renewals.is_new_count
                  : 0,
            },
            cancellations: {
              count:
                typeof data.cancellations?.count === 'number'
                  ? data.cancellations.count
                  : typeof data.cancellations === 'number'
                    ? data.cancellations
                    : 0,
              is_new_count:
                typeof data.cancellations?.is_new_count === 'number'
                  ? data.cancellations.is_new_count
                  : 0,
            },
            document_score:
              typeof data.document_score === 'number' ? data.document_score : 0,
          };
          setStatusData(processedData);
        }
      } catch (error) {
        console.error('Failed to fetch status data:', error);
        // Keep existing dash values on error
      }
    };

    fetchStatusData();
  }, []);

  return (
    <Box p="15px">
      <Grid
        templateColumns={{ base: '1fr', lg: '1fr 300px' }}
        gap="15px"
        h="100%"
      >
        {/* Main Content */}
        <GridItem>
          <VStack spacing="12px" align="stretch">
            <PermissionWrapper cardId="sales-dashboard">
              <SalesContestDashboard />
            </PermissionWrapper>

            <Card bg={bgColor} p="15px" borderRadius="16px">
              <Flex justify="space-between" align="center" w="100%">
                <HStack spacing="12px">
                  <Button
                    leftIcon={<Icon as={FaChartSimple} />}
                    colorScheme="brand"
                    size="md"
                    borderRadius="12px"
                  >
                    Analytics
                  </Button>
                  <HStack spacing="10px">
                    <Text fontSize="sm" color={textColor}>
                      MP View
                    </Text>
                    <Switch colorScheme="brand" size="md" />
                  </HStack>
                </HStack>

                {/* Gold Broker badge moved here */}
                <Card
                  bg="linear-gradient(135deg, #FFD700 0%, #FFA500 100%)"
                  p="8px 12px"
                  borderRadius="12px"
                  textAlign="center"
                  color="white"
                  position="relative"
                  overflow="hidden"
                  maxH="50px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width="fit-content"
                  zIndex="0"
                >
                  <HStack spacing="8px" position="relative" zIndex="1">
                    <Icon as={FaTrophy} boxSize="20px" />
                    <Text fontSize="sm" fontWeight="bold">
                      Gold Broker
                    </Text>
                  </HStack>
                  <Box
                    position="absolute"
                    top="-5px"
                    right="-5px"
                    opacity="0.15"
                    transform="rotate(15deg)"
                  >
                    <Icon as={FaTrophy} boxSize="25px" />
                  </Box>
                </Card>
              </Flex>
            </Card>

            {/* Broker Health - Consolidated Section */}
            <Card bg={bgColor} p="15px" borderRadius="16px" position="relative">
              {/* Coming Soon Label at Top */}
              <Text
                fontSize="sm"
                color="gray.500"
                fontStyle="italic"
                textAlign="center"
                mb="10px"
              >
                Coming Soon
              </Text>
              {/* Gauges Section - 2 Column Layout */}
              <Grid
                templateColumns={{ base: '1fr', md: '1fr 1fr' }}
                gap="15px"
                mb="15px"
              >
                {/* Left Column - Broker Health (Large) */}
                <Box textAlign="center">
                  <Text
                    fontSize="md"
                    color={textColor}
                    mb="8px"
                    fontWeight="600"
                  >
                    Broker Health
                  </Text>
                  <Flex w="100%" justify="center">
                    <GaugeComponent
                      value={0}
                      label=""
                      isLarge={true}
                      disabled={true}
                    />
                  </Flex>
                </Box>

                {/* Right Column - 2x2 Grid of Other Gauges */}
                <Grid templateColumns="repeat(2, 1fr)" gap="12px">
                  <Box textAlign="center">
                    <Text
                      fontSize="xs"
                      color={textColor}
                      mb="5px"
                      fontWeight="600"
                    >
                      Sales
                    </Text>
                    <GaugeComponent value={0} label="" disabled={true} />
                  </Box>

                  <Box textAlign="center">
                    <Text
                      fontSize="xs"
                      color={textColor}
                      mb="5px"
                      fontWeight="600"
                    >
                      Service
                    </Text>
                    <GaugeComponent value={0} label="" disabled={true} />
                  </Box>

                  <Box textAlign="center">
                    <Text
                      fontSize="xs"
                      color={textColor}
                      mb="5px"
                      fontWeight="600"
                    >
                      Document Score
                    </Text>
                    <GaugeComponent
                      value={Math.round(statusData.document_score ?? 0)}
                      label=""
                    />
                  </Box>

                  <Box textAlign="center">
                    <Text
                      fontSize="xs"
                      color={textColor}
                      mb="5px"
                      fontWeight="600"
                    >
                      Performance to Plan
                    </Text>
                    <GaugeComponent value={0} label="" disabled={true} />
                  </Box>
                </Grid>
              </Grid>

              {/* Metric Cards Row - More Compact */}
              <Grid
                templateColumns={{
                  base: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                }}
                gap="10px"
              >
                <MetricCard title="Book Size" value="$0" disabled={true} />
                <MetricCard title="Personal Lines" value="$0" disabled={true} />
                <MetricCard title="CL" value="$0" disabled={true} />
                <MetricCard title="L & F" value="$0" disabled={true} />
              </Grid>
            </Card>

            {/* Broker's Desk Section */}
            <Box>
              {/* Clickable Desk Header */}
              <Flex
                align="center"
                mb="8px"
                cursor="pointer"
                onClick={() => setIsDeskExpanded(!isDeskExpanded)}
                _hover={{ opacity: 0.8 }}
                transition="opacity 0.2s"
                userSelect="none"
              >
                <Text
                  fontSize="lg"
                  color={textColor}
                  fontWeight="700"
                  mr="12px"
                >
                  {userData
                    ? `${userData.first_name} ${userData.last_name}'s Desk`
                    : "Broker's Desk"}
                </Text>

                {/* Rotating Arrow Icon */}
                <Icon
                  as={FiChevronDown}
                  w="24px"
                  h="24px"
                  color={textColor}
                  transform={isDeskExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
                  transition="transform 0.3s ease"
                />
              </Flex>

              {/* Collapsible Status Boxes */}
              <Box
                maxH={isDeskExpanded ? '500px' : '0px'}
                overflow="hidden"
                transition="max-height 0.4s ease-in-out"
              >
                <Card bg={bgColor} p="20px" borderRadius="16px">
                  <Grid
                    templateColumns={{
                      base: 'repeat(2, 1fr)',
                      md: 'repeat(7, 1fr)',
                    }}
                    gap="20px"
                    justifyItems="center"
                    alignItems="start"
                  >
                    <StatusBox
                      count={statusData.leads.count}
                      label="Leads"
                      isNewCount={statusData.leads.is_new_count}
                      onClick={() => navigate('/admin/leads/list')}
                    />
                    <StatusBox
                      count={statusData.work_in_progress.count}
                      label="Work In Progress"
                      isNewCount={statusData.work_in_progress.is_new_count}
                      onClick={() =>
                        navigate('/admin/policies/work-in-progress')
                      }
                    />
                    <StatusBox
                      count={statusData.binders.count}
                      label="Binders"
                      isNewCount={statusData.binders.is_new_count}
                      onClick={() => navigate('/admin/policies/list')}
                    />
                    <StatusBox
                      count={statusData.documents.count}
                      label="Documents"
                      isNewCount={statusData.documents.is_new_count}
                      onClick={() => navigate('/admin/policies/documents')}
                    />
                    <StatusBox
                      count={statusData.tasks.count}
                      label="Tasks"
                      isNewCount={statusData.tasks.is_new_count}
                      isComingSoon={true}
                    />
                    <StatusBox
                      count={statusData.renewals.count}
                      label="Renewals"
                      isNewCount={statusData.renewals.is_new_count}
                      isComingSoon={true}
                    />
                    <StatusBox
                      count={statusData.cancellations.count}
                      label="Cancellations"
                      isNewCount={statusData.cancellations.is_new_count}
                      isComingSoon={true}
                    />
                  </Grid>
                </Card>
              </Box>
            </Box>
          </VStack>
        </GridItem>

        {/* Important Updates Feed */}
        <GridItem>
          <Card
            bg={bgColor}
            borderRadius="16px"
            border="1px solid"
            borderColor={borderColor}
            h="600px"
            overflow="hidden"
            display="flex"
            flexDirection="column"
          >
            <Box
              p="20px"
              borderBottom="1px solid"
              borderColor={borderColor}
              flexShrink={0}
            >
              <Text fontSize="lg" fontWeight="bold" color={textColor}>
                Important Updates
              </Text>
            </Box>

            <Box
              flex="1"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text fontSize="lg" color={textColor}>
                Coming soon
              </Text>
            </Box>
          </Card>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default Home;
